import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import propTypes from 'prop-types';

import './dealers.scss';

import { useConfig, useDeviceType, useMap } from '../../hooks';

import { updateUrl } from '../../lib/utils';

import Map from './Map';
import Dealers from './Dealers';
import Dialog from '../Dialog';
import Dealer from './Dealers/Dealer';

function MapAndDealers({ initialDealer, location, userLocation, onLocationChange, onUserLocationChange }) {
  const { config } = useConfig();
  const { isMobile } = useDeviceType();
  const ref = useRef(null);
  const { setMapVisible } = useMap();

  const [initialLocation, setInitialLocation] = useState(location || null);
  const [isInitial, setIsInitial] = useState(!!initialDealer);
  const [activeDealer, setActiveDealer] = useState(initialDealer);
  const [dealerOpen, setDealerOpen] = useState(!!initialDealer);
  const [selectedFilters, setSelectedFilters] = useState(config.filters.filter(item => item.active).map(item => item.value));
  const [disableTransition] = useState(!!initialDealer || ['address', 'city'].includes(location?.type) || config.skipLandingPage);

  if (!initialLocation && location) {
    setInitialLocation(location);
  }

  const mapComponent = useMemo(() => {
    return (
      <Map
        initialDealer={initialDealer}
        location={initialLocation}
        userLocation={userLocation}
        onUserLocationChange={onUserLocationChange}
      />
    );
  }, [initialDealer, initialLocation, userLocation, onUserLocationChange]);

  function scrollToTop(behavior) {
    if (!ref.current) {
      return;
    }

    const top = ref.current.getBoundingClientRect().top;
    window.scrollTo({
      top: window.scrollY + top - (document.querySelector('[data-main-navigation]')?.offsetHeight  || 0),
      behavior: behavior || 'smooth'
    });
  }

  const handleDealerSelect = useCallback((dealer) => {
    setActiveDealer(dealer);
    setDealerOpen(true);
    if (isMobile) {
      setMapVisible(false);
    }

    if (isMobile) {
      scrollToTop('auto');
    }

    // Move focus to popover after dealer is opened
    setTimeout(() => {
      ref.current.querySelector('.single-dealer [data-back-to-list]')?.focus();

      if (isMobile) {
        scrollToTop('auto');
      }
    }, 10);
  }, [isMobile]);

  const handleDealerClose = useCallback(() => {
    setIsInitial(false);
    setDealerOpen(false);
    setTimeout(() => {
      setActiveDealer(null);

      // Move focus to item that was opened
      setTimeout(() => {
        ref.current.querySelector(`.dealer[data-dealer="${activeDealer.uuid}"]`)?.focus();
        if (isMobile) {
          scrollToTop();
        }
      }, 10);
    }, 200);
  }, [activeDealer, isMobile]);

  const handleFilterChange = useCallback((filters) => {
    setSelectedFilters(filters);
  }, []);

  useEffect(() => {
    if (!disableTransition) {
      setTimeout(() => {
        ref.current.classList.add('visible');
      }, 10);
    }
    if (isMobile) {
      scrollToTop();
    }
  }, []);

  useEffect(() => {
    const activeFilters = selectedFilters.map(filterId => config.filters.find(item => item.value === filterId).apiValue).filter(Boolean);

    updateUrl({
      address: ['address'].includes(location?.type) ? location.address : '',
      slug: activeDealer ? activeDealer.slug : null,
      dealerName: activeDealer?.company_name,
      skipLandingPage: config.skipLandingPage && config.mapType === 'default',
      filters: activeFilters,
    });
  }, [activeDealer, selectedFilters, config, location]);

  return (
    <div ref={ref} className={'dealers-container' + (disableTransition ? ' no-transition' : '')} data-hj-suppress>
      <div className="dealers-grid">
        {mapComponent}
        <Dealers
          activeDealer={activeDealer}
          location={location}
          userLocation={userLocation}
          selectedFilters={selectedFilters}
          onLocationChange={onLocationChange}
          onDealerSelect={handleDealerSelect}
          onFilterChange={handleFilterChange}
          onUserLocationChange={onUserLocationChange}
        />
      </div>

      <Dialog type={'single-dealer'} animation={isInitial ? 'none' : 'default'} open={dealerOpen} onClose={handleDealerClose}>
        {activeDealer && (
          <Dealer dealer={activeDealer} userLocation={userLocation} onActiveDealerChange={handleDealerClose} isFromUrl={!!initialDealer} />
        )}
      </Dialog>
    </div>
  );
}

MapAndDealers.propTypes = {
  initialStreetAddress: propTypes.string,
  initialDealer: propTypes.object,
  initialFilters: propTypes.array,
  location: propTypes.object,
  userLocation: propTypes.object,
  onLocationChange: propTypes.func,
  onUserLocationChange: propTypes.func,
};

export default MapAndDealers;
